<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { getApostatudoApiUrl } from '~/utils/apostatudo/get-apostatudo-api-url';
import { getApostatudoRequestHeaders } from '~/utils/apostatudo/get-apostatudo-request-headers';

// Seção "Estatísticas" do dashboard Perfil — redesenhada pro Figma node
// 23508:113345 ("bonus-cards"): 3 cards de bônus recebido (giros, cashback,
// saldo), cada um com gradiente/cor/ícone próprios. Substitui o grid antigo
// de 6 tiles genéricos (Figma 19771:42296/19771:52185) — os outros 3 stats
// (Ganhos totais, Apostas totais, Vitórias) saíram, não têm mais lugar
// nessa tela. Extraída do ScreenProfile.vue pra não inflar mais aquele
// arquivo; autocontida, mesmo padrão do UserV2BalancesBar.

// Ícones baixados do Figma (não existiam no projeto, ver public/images/
// user-v2/stat-{spins,cashback,bonus}.png) e redimensionados pra 120x120,
// mesmo padrão do stat-trophy.png que eles substituem.
//
// Mobile (node 23508:113391) tem layout diferente do desktop (vertical,
// gradiente radial, ícone grande embaixo) e o Figma usa cor levemente
// diferente do desktop pro rótulo de cashback (#4ade80, não #86efac).
// Mantive as duas versões fiéis ao que está desenhado em cada breakpoint,
// mesmo com a pequena inconsistência entre elas (parece só deriva do
// próprio Figma).

// GET /players/bonus (identidade pelo Bearer, ENG-497) — lifetime + janela de
// 7 dias + janela de 12 meses do jogador. Chamado toda vez que o modal abre: a screen desmonta/
// remonta a cada open/close (ver Modal.vue), então o onMounted abaixo já
// cobre isso sozinho. Mesma URL/auth de /players/me (Apostatudo API, não o
// BFF genérico) — getApostatudoApiUrl()/getApostatudoRequestHeaders(), ver
// adapters/api-player-profile/apostatudo-api-fetch-player-profile.adapter.ts.
interface PlayerBonusEntry {
  amount: string;
}

interface PlayerBonus {
  user_id: number;
  lifetime: {
    cashback: string;
    bonus_balance: string;
    free_spins: string;
  };
  days: Array<PlayerBonusEntry & { date: string }>;
  months: Array<PlayerBonusEntry & { month: string }>;
}

const { fetchUserProfile } = useUserData();

const playerBonus = ref<PlayerBonus | null>(null);
const isLoadingBonus = ref(true);

async function getPlayerBonus() {
  isLoadingBonus.value = true;

  await fetch(`${getApostatudoApiUrl()}/players/bonus`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...getApostatudoRequestHeaders(),
    },
  })
    .then((response) => {
      if (!response.ok)
        throw new Error(`PlayerBonus failed: ${response.status}`);

      return response.json();
    })
    .then((data) => {
      playerBonus.value = data;
    })
    .catch(() => {
      playerBonus.value = null;
    })
    .finally(() => {
      isLoadingBonus.value = false;
    });
}

onMounted(async () => {
  await fetchUserProfile();
  getPlayerBonus();
});

// nunca 404 — quem nunca teve bônus vem com days/months vazios (sempre
// juntos). Não é erro nem empty state: a seção renderiza ZERADA (cards em
// R$ 0,00 e gráfico reto no zero), com uma série sintética de 7 dias / 12
// meses terminando hoje pra o gráfico ter eixo X e pontos como sempre.
// Também cobre falha na chamada (playerBonus null).
const PLACEHOLDER_DAYS = 7;
const PLACEHOLDER_MONTHS = 12;

// mesma ordem da API (mais recente primeiro) — buildGrowth inverte.
function placeholderDays() {
  const today = new Date();

  return Array.from({ length: PLACEHOLDER_DAYS }, (_, index) => {
    const date = new Date(today);

    date.setUTCDate(today.getUTCDate() - index);

    return { date: date.toISOString().slice(0, 10), amount: '0.00' };
  });
}

function placeholderMonths() {
  const today = new Date();

  return Array.from({ length: PLACEHOLDER_MONTHS }, (_, index) => {
    const month = new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - index, 1),
    );

    return { month: month.toISOString().slice(0, 10), amount: '0.00' };
  });
}

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

// dinheiro chega como string ("1.50"), nunca number — Number() só serve pra
// EXIBIR um valor por vez (o que o Intl.NumberFormat faz aqui). Nunca somar
// vários valores em Number, perde centavo.
function formatCurrency(amount: string) {
  return currencyFormatter.format(Number(amount));
}

// Folga extra (px) entre o valor "R$ ..." e a arte nos cards mobile, somada
// ao top do Figma de cada card. O bloco de texto termina em y=35 (6 + 14 + 1
// + 14) e os PNGs têm 1–3px de transparência no topo, então o que o olho vê
// de espaço é ~ (top + transparência − 33). 4px dá uma folga clara.
const MOBILE_ICON_GAP = 4;

const bonusCards = computed(() => {
  // lifetime some enquanto carrega/sem sessão — zera em vez de quebrar o
  // formatCurrency.
  const lifetime = playerBonus.value?.lifetime ?? {
    cashback: '0.00',
    bonus_balance: '0.00',
    free_spins: '0.00',
  };

  return [
    {
      label: 'Total giros',
      labelMobile: 'Ganho com giros',
      grow: 1,
      value: formatCurrency(lifetime.free_spins),
      labelColor: '#fde047',
      glow: '#F5C451',
      gradient:
        'linear-gradient(90deg, rgba(245,196,81,0) 0%, rgba(245,196,81,0.05) 55%, rgba(245,196,81,0.22) 100%), linear-gradient(90deg, #713F12 0%, #713F12 100%)',
      gradientMobile:
        'radial-gradient(120% 70% at 50% 100%, rgba(245,196,81,0.22) 0%, rgba(245,196,81,0.05) 45%, rgba(245,196,81,0) 100%), linear-gradient(90deg, #713F12 0%, #713F12 100%)',
      icon: '/images/user-v2/stat-spins.png',
      // mobile (Figma 23508:113392-4): cada card tem a arte num tamanho e
      // altura próprios — a do bônus é a maior e todas vazam pelo rodapé.
      // top é o do Figma; a folga extra entre o "R$" e a arte é
      // MOBILE_ICON_GAP, somada no template.
      iconSizeMobile: 69,
      iconTopMobile: 36,
      hasTooltip: false,
    },
    {
      // ENG-789: título ajustado (era "Cashback Total") — a fórmula por trás
      // agrega saldos reais + cashback + prêmios de torneio/missão/mini-game
      // do período, não é só cashback puro. Mesmo texto nos dois breakpoints
      // (a inconsistência antiga entre desktop/mobile não fazia sentido aqui,
      // já que os dois precisam bater com o tooltip).
      label: 'Saldo e cashback',
      labelMobile: 'Saldo e cashback',
      grow: 1,
      value: formatCurrency(lifetime.cashback),
      labelColor: '#86efac',
      labelColorMobile: '#4ade80',
      glow: '#17C964',
      gradient:
        'linear-gradient(90deg, rgba(23,201,100,0) 0%, rgba(23,201,100,0.05) 55%, rgba(23,201,100,0.22) 100%), linear-gradient(90deg, #14532D 0%, #14532D 100%)',
      gradientMobile:
        'radial-gradient(120% 70% at 50% 100%, rgba(23,201,100,0.22) 0%, rgba(23,201,100,0.05) 45%, rgba(23,201,100,0) 100%), linear-gradient(90deg, #14532D 0%, #14532D 100%)',
      icon: '/images/user-v2/stat-cashback.png',
      iconSizeMobile: 75,
      iconTopMobile: 32,
      // só este card explica a fórmula (ENG-789) — os outros dois já são
      // literais (giros recebidos, saldo bônus), não precisam de tooltip.
      hasTooltip: true,
    },
    {
      label: 'Saldo bônus',
      labelMobile: 'Saldo bônus',
      grow: 1,
      value: formatCurrency(lifetime.bonus_balance),
      labelColor: '#67e8f9',
      glow: '#42ACFF',
      gradient:
        'linear-gradient(90deg, rgba(66,172,255,0) 0%, rgba(66,172,255,0.05) 55%, rgba(66,172,255,0.22) 100%), linear-gradient(90deg, #0D0D90 0%, #0D0D90 100%)',
      gradientMobile:
        'radial-gradient(120% 70% at 50% 100%, rgba(66,172,255,0.22) 0%, rgba(66,172,255,0.05) 45%, rgba(66,172,255,0) 100%), linear-gradient(90deg, #0D0D90 0%, #0D0D90 100%)',
      icon: '/images/user-v2/stat-bonus.png',
      iconSizeMobile: 83,
      iconTopMobile: 32,
      hasTooltip: false,
    },
  ];
});

const MONTH_LABELS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

// `?? ''`: com noUncheckedIndexedAccess o índice devolve `string | undefined`
// e buildGrowth espera `string`. Só cai no '' se a data vier inválida (NaN).
function monthLabel(month: string) {
  return MONTH_LABELS[new Date(month).getUTCMonth()] ?? '';
}

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function weekdayLabel(date: string) {
  return WEEKDAY_LABELS[new Date(date).getUTCDay()] ?? '';
}

// entries vem do mais recente pro mais antigo — inverte pra desenhar da
// esquerda (mais antigo) pra direita (mais recente), como o gráfico espera.
function buildGrowth(
  entries: { amount: string }[],
  toLabel: (entry: any) => string,
) {
  const chronological = [...entries].reverse();

  // gráfico mostra o valor de CADA período isolado, não acumulado — só o
  // "Bônus do período" (delta) abaixo é a soma da janela inteira.
  const points = chronological.map((entry) => ({
    day: toLabel(entry),
    value: Math.max(0, Number(entry.amount)),
  }));

  const total = points.reduce((sum, point) => sum + point.value, 0);

  return {
    delta: `${total >= 0 ? '+' : '-'}${currencyFormatter.format(Math.abs(total))}`,
    points,
  };
}

const monthlyGrowth = computed(() => {
  const months = playerBonus.value?.months ?? [];

  return buildGrowth(months.length ? months : placeholderMonths(), (entry) =>
    monthLabel(entry.month),
  );
});

const weeklyGrowth = computed(() => {
  const days = playerBonus.value?.days ?? [];

  return buildGrowth(days.length ? days : placeholderDays(), (entry) =>
    weekdayLabel(entry.date),
  );
});

// dropdown "Semanal/Mensal" do header do gráfico — padrão mensal (obs do
// ENG-362), troca pra semanal (days) e volta.
const GROWTH_PERIOD_OPTIONS = [
  { value: 'monthly', label: 'Mensal' },
  { value: 'weekly', label: 'Semanal' },
] as const;

const growthPeriod = ref<'monthly' | 'weekly'>('monthly');

const growthPeriodLabel = computed(
  () =>
    GROWTH_PERIOD_OPTIONS.find((option) => option.value === growthPeriod.value)
      ?.label,
);

const periodBonus = computed(() =>
  growthPeriod.value === 'monthly' ? monthlyGrowth.value : weeklyGrowth.value,
);

// ----- Gráfico "Bônus do período" (SVG inline, sem lib) -----
const CHART_W = 303;
const CHART_H = 64;
const CHART_PAD_X = 7;
const CHART_PAD_Y = 8;

// eixo Y escalado pelo maior valor real (antes assumia range fixo 0-100)
const chartMaxValue = computed(() =>
  Math.max(...periodBonus.value.points.map((point) => point.value), 1),
);

const chartPoints = computed(() =>
  periodBonus.value.points.map((point, index) => ({
    day: point.day,
    value: point.value,
    x:
      CHART_PAD_X +
      (index * (CHART_W - CHART_PAD_X * 2)) /
        (periodBonus.value.points.length - 1),
    y:
      CHART_H -
      CHART_PAD_Y -
      (point.value / chartMaxValue.value) * (CHART_H - CHART_PAD_Y * 2),
  })),
);

const chartLine = computed(() =>
  chartPoints.value.map((p) => `${p.x},${p.y}`).join(' '),
);

const chartArea = computed(
  () =>
    `${CHART_PAD_X},${CHART_H} ${chartLine.value} ${CHART_W - CHART_PAD_X},${CHART_H}`,
);

// tooltip do card "Saldo e cashback" (ENG-789, texto do Figma node
// 19771:52161). Mesma interação do balão do delta abaixo (hover desktop,
// toggle de clique mobile) — mas os cards têm overflow-hidden (pro glow/PNG
// não vazar dos cantos arredondados), então o balão em si teleporta pro
// <body> como fixed; senão seria cortado pelo próprio card. Posição calculada
// na hora do open a partir do elemento clicado/hover — não recalcula em
// scroll/resize (some ao sair do hover, e é raro rolar segurando um toggle
// mobile aberto).
const CASHBACK_TOOLTIP_TEXT =
  'Essa função mostra todos seus saldos reais e cashbacks, seja entrega de prêmio de torneios, missões ou mini-games recebidos no período total da sua conta.';

// balão é fixed w-[240px] (Figma node 23790:24342, get_metadata confirma
// width=240) centrado em `left` via -translate-x-1/2 — sem clamp, `left`
// cru (posição do ícone) estoura a janela quando o ícone está perto da
// borda direita (era o bug: balão existia no DOM, só renderizava fora da
// área visível). Limitado pra sempre caber entre as duas margens.
const CASHBACK_TOOLTIP_WIDTH = 240;
const CASHBACK_TOOLTIP_MARGIN = 8;

// a seta aponta pro ÍCONE, não pro centro do balão — margem mínima da borda
// arredondada do balão pra ela nunca colar no canto.
const ARROW_EDGE_MARGIN = 16;

const showCashbackTooltip = ref(false);
const cashbackTooltipPosition = ref({ top: 0, left: 0, arrowLeft: 0 });

function openCashbackTooltip(event: MouseEvent) {
  // dispara tanto pelo ícone (hover no desktop) quanto pelo card inteiro
  // (clique em qualquer lugar dele, nos dois breakpoints) — normaliza pro
  // CARD, que é quem sempre é o alvo do clique OU o pai direto do ícone.
  const target = event.currentTarget as HTMLElement;
  const card =
    target.tagName === 'BUTTON' ? (target.parentElement ?? target) : target;

  const icon = card.querySelector('button');
  const cardRect = card.getBoundingClientRect();
  const iconRect = (icon ?? card).getBoundingClientRect();
  const halfWidth = CASHBACK_TOOLTIP_WIDTH / 2;
  const rawLeft = cardRect.left + cardRect.width / 2;
  const minLeft = halfWidth + CASHBACK_TOOLTIP_MARGIN;
  const maxLeft = window.innerWidth - halfWidth - CASHBACK_TOOLTIP_MARGIN;
  const left = Math.min(Math.max(rawLeft, minLeft), maxLeft);

  // posição da seta = distância do ícone até a borda esquerda do balão JÁ
  // clampado (não do card) — senão ela desalinha quando o balão é empurrado
  // pra caber na tela. Coordenada absoluta (arrowLeft), não offset relativo:
  // a seta é elemento IRMÃO do balão (não filho), fica atrás dele (z-index
  // menor) pra caixa opaca do balão cobrir a metade "de dentro" sozinha.
  const balloonLeftEdge = left - halfWidth;
  const iconCenterX = iconRect.left + iconRect.width / 2;
  const arrowOffset = Math.min(
    Math.max(iconCenterX - balloonLeftEdge, ARROW_EDGE_MARGIN),
    CASHBACK_TOOLTIP_WIDTH - ARROW_EDGE_MARGIN,
  );

  cashbackTooltipPosition.value = {
    top: cardRect.bottom + 6,
    left,
    arrowLeft: balloonLeftEdge + arrowOffset,
  };
  showCashbackTooltip.value = true;
}

function closeCashbackTooltip() {
  showCashbackTooltip.value = false;
}

// clique fora fecha (mobile não tem mouseleave). ignore os cards inteiros
// (mobile e desktop, só um visível por vez) — senão o clique que ABRE o
// tooltip (no card ou no ícone, que é filho dele) também contaria como "fora
// do balão" e fecharia na mesma hora. Function ref: v-for tem 3 cards, só
// interessa capturar o de cashback (card.hasTooltip), não os outros dois.
const cashbackTooltipRef = ref<HTMLElement | null>(null);
const cashbackCardMobileRef = ref<HTMLElement | null>(null);
const cashbackCardDesktopRef = ref<HTMLElement | null>(null);

onClickOutside(cashbackTooltipRef, () => closeCashbackTooltip(), {
  ignore: [cashbackCardMobileRef, cashbackCardDesktopRef],
});

// balão explicando o delta — hover no desktop, toggle de clique no mobile
// (não tem :hover de verdade em touch, então o clique cobre os dois).
const showDeltaHint = ref(false);

const deltaHintText = computed(() =>
  growthPeriod.value === 'monthly'
    ? 'Soma do bônus ganho nos últimos 12 meses.'
    : 'Soma do bônus ganho nos últimos 7 dias.',
);

// tooltip com o valor do ponto, ao passar o mouse
const hoveredPointIndex = ref<number | null>(null);

const hoveredPoint = computed(() =>
  hoveredPointIndex.value !== null
    ? chartPoints.value[hoveredPointIndex.value]
    : null,
);
</script>

<template>
  <section class="flex w-full flex-col gap-[10px]">
    <div
      class="flex h-[32px] w-full items-center rounded-[12px] bg-v2-default-300 pl-[12px]"
    >
      <!-- mesmo texto e mesma tipografia do cabeçalho "Atalhos" (ScreenProfile)
           nos dois breakpoints: 14px no mobile, 12px no desktop -->
      <p
        class="text-[14px] font-medium leading-[16px] text-white md:text-[12px]"
      >
        Estatísticas e bônus recebidos
      </p>
    </div>

    <div
      class="border-solid flex w-full flex-col gap-[10px] rounded-[16px] border border-v2-default-300 p-[16px]"
    >
      <!-- loading -->
      <template v-if="isLoadingBonus">
        <div class="flex w-full gap-[10px]">
          <div
            v-for="n in 3"
            :key="n"
            class="h-[92px] flex-1 animate-pulse rounded-[12px] bg-v2-default/40 lg:h-[64px]"
          />
        </div>
        <div
          class="h-[120px] w-full animate-pulse rounded-[12px] bg-v2-default/40"
        />
      </template>

      <!-- sem bônus (days/months vazios) ou falha: renderiza zerado, ver
           placeholderDays/placeholderMonths no script -->
      <template v-else>
        <!-- mobile (node 23508:113391): vertical, gradiente radial, ícone
             grande embaixo — layout bem diferente do desktop pra caber a
             mesma info num card mais estreito -->
        <div class="flex w-full gap-[10px] lg:hidden">
          <div
            v-for="card in bonusCards"
            :key="card.label"
            :ref="
              (el) => {
                if (card.hasTooltip) cashbackCardMobileRef = el as HTMLElement;
              }
            "
            class="stat-card-mobile relative h-[92px] min-w-0 flex-1 overflow-hidden rounded-[12px]"
            :style="{ backgroundImage: card.gradientMobile }"
            @click="
              card.hasTooltip &&
              (showCashbackTooltip
                ? closeCashbackTooltip()
                : openCashbackTooltip($event))
            "
          >
            <div
              class="absolute inset-x-[8px] top-[6px] z-10 flex flex-col items-center gap-[1px]"
            >
              <p
                class="stat-card-mobile-label h-[14px] whitespace-nowrap text-center font-bold leading-[14px]"
                :style="{ color: card.labelColorMobile || card.labelColor }"
              >
                {{ card.labelMobile }}
              </p>
              <p
                class="whitespace-nowrap text-[11px] font-extrabold leading-[14px] text-white"
              >
                {{ card.value }}
              </p>
            </div>

            <!-- glow 77px centrado em y=70 (metade pra fora do card) + a arte
                 com o tamanho/posição de cada card no Figma, cortada pelo
                 overflow-hidden — mesmos PNGs do desktop, só a geometria muda -->
            <div
              aria-hidden="true"
              class="pointer-events-none absolute left-1/2 top-[70px] size-[77px] -translate-x-1/2 rounded-full opacity-45 blur-[14px]"
              :style="{ backgroundColor: card.glow }"
            />
            <img
              alt=""
              class="pointer-events-none absolute left-1/2 -translate-x-1/2 object-contain"
              :src="getAssetsUrlForCDN(card.icon)"
              :style="{
                width: `${card.iconSizeMobile}px`,
                height: `${card.iconSizeMobile}px`,
                top: `${card.iconTopMobile + MOBILE_ICON_GAP}px`,
              }"
            />

            <!-- ENG-789: só o card "Saldo e cashback" tem tooltip. Mobile
                 (Figma 23508:113393): canto INFERIOR direito, diferente do
                 desktop (superior) — o card aqui é vertical, a arte ocupa a
                 metade de baixo. Sem @click aqui: o card inteiro (acima) já
                 cobre o clique, inclusive quando cai em cima do ícone (o
                 clique borbulha pro card, que dispara o mesmo handler). -->
            <button
              v-if="card.hasTooltip"
              class="pointer-events-none absolute bottom-[7px] right-[4px] z-20"
              tabindex="-1"
              type="button"
            >
              <svg
                fill="none"
                height="13"
                viewBox="0 0 13 13"
                width="13"
              >
                <path
                  clip-rule="evenodd"
                  d="M6.2692 10.5794C8.6496 10.5794 10.5793 8.64966 10.5793 6.26926C10.5793 3.88886 8.6496 1.95917 6.2692 1.95917C3.8888 1.95917 1.95911 3.88886 1.95911 6.26926C1.95911 8.64966 3.8888 10.5794 6.2692 10.5794ZM6.2692 11.7548C9.2988 11.7548 11.7548 9.29886 11.7548 6.26926C11.7548 3.23967 9.2988 0.783691 6.2692 0.783691C3.2396 0.783691 0.78363 3.23967 0.78363 6.26926C0.78363 9.29886 3.2396 11.7548 6.2692 11.7548ZM7.05286 8.2284C7.05286 8.66119 6.702 9.01205 6.2692 9.01205C5.8364 9.01205 5.48555 8.66119 5.48555 8.2284C5.48555 7.7956 5.8364 7.44474 6.2692 7.44474C6.702 7.44474 7.05286 7.7956 7.05286 8.2284ZM6.85694 3.9183C6.85694 3.5937 6.5938 3.33056 6.2692 3.33056C5.9446 3.33056 5.68146 3.5937 5.68146 3.9183V5.87744C5.68146 6.20204 5.9446 6.46518 6.2692 6.46518C6.5938 6.46518 6.85694 6.20204 6.85694 5.87744V3.9183Z"
                  fill="#FFFFFF"
                  fill-rule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- desktop (node 23508:113345): horizontal, gradiente linear, ícone
           pequeno no canto -->
        <div class="hidden w-full gap-[10px] lg:flex">
          <div
            v-for="card in bonusCards"
            :key="card.label"
            :ref="
              (el) => {
                if (card.hasTooltip) cashbackCardDesktopRef = el as HTMLElement;
              }
            "
            class="relative flex min-w-0 basis-0 items-center gap-[8px] overflow-hidden rounded-[12px] p-[10px]"
            :class="{ 'cursor-pointer': card.hasTooltip }"
            :style="{ backgroundImage: card.gradient, flexGrow: card.grow }"
            @click="
              card.hasTooltip &&
              (showCashbackTooltip
                ? closeCashbackTooltip()
                : openCashbackTooltip($event))
            "
            @mouseenter="card.hasTooltip && openCashbackTooltip($event)"
            @mouseleave="card.hasTooltip && closeCashbackTooltip()"
          >
            <!-- pr reserva a faixa do ícone (54px a -6px da borda, menos o
                 padding do card) pro texto não correr por baixo dele -->
            <div class="flex min-w-0 flex-1 flex-col gap-[5px] pr-[40px]">
              <p
                class="whitespace-nowrap text-[12px] font-bold leading-none"
                :style="{ color: card.labelColor }"
              >
                {{ card.label }}
              </p>
              <p
                class="truncate text-[14px] font-extrabold leading-none text-white"
              >
                {{ card.value }}
              </p>
            </div>

            <!-- glow: mancha desfocada atrás do ícone — CSS em vez de baixar
               o SVG do Figma, é só um círculo com blur, mais barato -->
            <div
              aria-hidden="true"
              class="pointer-events-none absolute -right-[10px] top-[8px] size-[54px] rounded-full opacity-45 blur-xl"
              :style="{ backgroundColor: card.glow }"
            />
            <img
              alt=""
              class="pointer-events-none absolute -right-[6px] top-0 size-[54px] object-contain"
              :src="getAssetsUrlForCDN(card.icon)"
            />

            <!-- ENG-789: só o card "Saldo e cashback" tem tooltip. Botão
                 100% visual (tabindex -1, sem listener) — clique E hover
                 cobrem o card inteiro (acima), não só o ícone. -->
            <button
              v-if="card.hasTooltip"
              class="pointer-events-none absolute right-[4px] top-[4px] z-20"
              tabindex="-1"
              type="button"
            >
              <svg
                fill="none"
                height="13"
                viewBox="0 0 13 13"
                width="13"
              >
                <path
                  clip-rule="evenodd"
                  d="M6.2692 10.5794C8.6496 10.5794 10.5793 8.64966 10.5793 6.26926C10.5793 3.88886 8.6496 1.95917 6.2692 1.95917C3.8888 1.95917 1.95911 3.88886 1.95911 6.26926C1.95911 8.64966 3.8888 10.5794 6.2692 10.5794ZM6.2692 11.7548C9.2988 11.7548 11.7548 9.29886 11.7548 6.26926C11.7548 3.23967 9.2988 0.783691 6.2692 0.783691C3.2396 0.783691 0.78363 3.23967 0.78363 6.26926C0.78363 9.29886 3.2396 11.7548 6.2692 11.7548ZM7.05286 8.2284C7.05286 8.66119 6.702 9.01205 6.2692 9.01205C5.8364 9.01205 5.48555 8.66119 5.48555 8.2284C5.48555 7.7956 5.8364 7.44474 6.2692 7.44474C6.702 7.44474 7.05286 7.7956 7.05286 8.2284ZM6.85694 3.9183C6.85694 3.5937 6.5938 3.33056 6.2692 3.33056C5.9446 3.33056 5.68146 3.5937 5.68146 3.9183V5.87744C5.68146 6.20204 5.9446 6.46518 6.2692 6.46518C6.5938 6.46518 6.85694 6.20204 6.85694 5.87744V3.9183Z"
                  fill="#FFFFFF"
                  fill-rule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- Bônus do período -->
        <div
          class="flex w-full flex-col items-center gap-[6px] rounded-[12px] bg-v2-default-300 px-[6px] pb-[12px] pt-[6px]"
        >
          <div class="flex w-full items-center justify-between pl-[6px]">
            <div
              class="flex items-center gap-[6px] text-[12px] font-bold leading-[16px]"
            >
              <p class="text-white">Bônus do período</p>
              <p class="text-v2-success">
                {{ periodBonus.delta }}
              </p>
              <span
                class="relative inline-flex size-[14px] shrink-0 items-center justify-center"
                @click="showDeltaHint = !showDeltaHint"
                @mouseenter="showDeltaHint = true"
                @mouseleave="showDeltaHint = false"
              >
                <svg
                  class="size-[12px] text-white/40"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  viewBox="0 0 16 16"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="7"
                  />
                  <line
                    x1="8"
                    x2="8"
                    y1="7"
                    y2="11.5"
                  />
                  <line
                    x1="8"
                    x2="8"
                    y1="4.5"
                    y2="4.5"
                  />
                </svg>

                <div
                  v-if="showDeltaHint"
                  class="absolute right-0 top-full z-10 mt-[6px] w-[180px] max-w-[70vw] rounded-[8px] bg-v2-default-500 p-[8px] text-[11px] font-normal leading-[15px] text-white lg:right-auto lg:left-0 lg:max-w-none"
                >
                  {{ deltaHintText }}
                </div>
              </span>
            </div>
            <!-- dropdown custom (Headless UI, já usado em base/form/Select.vue) —
               select nativo não deixa estilizar borda/raio da lista aberta -->
            <HeadlessListbox v-model="growthPeriod">
              <div class="relative">
                <HeadlessListboxButton
                  class="flex cursor-pointer items-center gap-[5px] rounded-[9px] px-[10px] py-[6px] text-[12px] font-semibold leading-[16px] text-v2-cyan-600 outline-none"
                >
                  {{ growthPeriodLabel }}
                  <svg
                    class="size-[10px]"
                    fill="none"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    viewBox="0 0 10 10"
                  >
                    <path d="m2 3.5 3 3 3-3" />
                  </svg>
                </HeadlessListboxButton>

                <HeadlessListboxOptions
                  class="border-solid absolute right-0 z-10 mt-[6px] flex w-[110px] flex-col gap-[4px] overflow-hidden rounded-[12px] border-[0.5px] border-v2-default-500 bg-v2-default-300 p-[4px] outline-none"
                >
                  <HeadlessListboxOption
                    v-for="option in GROWTH_PERIOD_OPTIONS"
                    :key="option.value"
                    v-slot="{ active, selected }"
                    as="template"
                    :value="option.value"
                  >
                    <li
                      class="cursor-pointer rounded-[8px] px-[10px] py-[8px] text-[12px] font-semibold"
                      :class="{
                        'bg-v2-primary text-white': selected,
                        'bg-white/10 text-white': active && !selected,
                        'text-v2-foreground': !active && !selected,
                      }"
                    >
                      {{ option.label }}
                    </li>
                  </HeadlessListboxOption>
                </HeadlessListboxOptions>
              </div>
            </HeadlessListbox>
          </div>

          <div class="w-full px-[6px]">
            <div
              class="relative w-full"
              :style="{ height: `${CHART_H}px` }"
            >
              <svg
                class="absolute inset-0 size-full"
                preserveAspectRatio="none"
                :viewBox="`0 0 ${CHART_W} ${CHART_H}`"
              >
                <polygon
                  class="fill-v2-success/15"
                  :points="chartArea"
                />
                <polyline
                  class="stroke-v2-success"
                  fill="none"
                  :points="chartLine"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  vector-effect="non-scaling-stroke"
                />
              </svg>
              <span
                v-for="(point, index) in chartPoints"
                :key="point.day"
                class="absolute size-[8px] -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full bg-v2-success"
                :style="{
                  left: `${(point.x / CHART_W) * 100}%`,
                  top: `${point.y}px`,
                }"
                @mouseenter="hoveredPointIndex = index"
                @mouseleave="hoveredPointIndex = null"
              />
              <div
                v-if="hoveredPoint"
                class="pointer-events-none absolute -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-[6px] bg-v2-default-500 px-[6px] py-[2px] text-[10px] font-semibold text-white"
                :style="{
                  left: `${(hoveredPoint.x / CHART_W) * 100}%`,
                  top: `${hoveredPoint.y - 8}px`,
                }"
              >
                {{ currencyFormatter.format(hoveredPoint.value) }}
              </div>
            </div>
            <div
              class="mt-[4px] flex w-full items-center justify-between text-center text-[10px] font-semibold leading-[16px] text-[rgba(212,212,216,0.4)]"
            >
              <p
                v-for="point in chartPoints"
                :key="point.day"
              >
                {{ point.day }}
              </p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </section>

  <!-- teleporta pro body: os cards são overflow-hidden (pro glow/PNG não
       vazar dos cantos), então o balão cortaria se ficasse dentro deles. O
       Modal.vue (UserV2Shell) TAMBÉM teleporta pro body com z-[90] — como os
       dois viram filhos diretos do body, quem decide quem fica por cima é
       z-index, não ordem no DOM. z-[100] pra ficar acima do modal inteiro. -->
  <Teleport to="body">
    <template v-if="showCashbackTooltip">
      <!-- setinha apontando pro card (Figma node 23790:24342) — quadrado
           rotacionado 45°, mesma cor do balão. IRMÃ do balão, não filha, com
           z-index MAIOR de propósito: fica NA FRENTE do balão, cobrindo (com
           o próprio preenchimento, mesma cor) o trecho da borda horizontal
           do balão que passaria embaixo dela — daí não corta mais o
           quadrado. Borda só em cima+esquerda: com rotate(45deg) no sentido
           horário, são as arestas TOP e LEFT do quadrado (não retas!) que se
           encontram na ponta de cima e formam o "^" visível — TOP vira a
           aresta de cima-direita do losango, LEFT vira a de cima-esquerda.
           RIGHT/BOTTOM viram as arestas de baixo (dentro do balão) e ficam
           sem borda, fundindo com o preenchimento. -->
      <div
        aria-hidden="true"
        class="border-solid pointer-events-none fixed z-[101] size-[8px] -translate-x-1/2 rotate-45 rounded-[2px] border-l border-t border-white/10 bg-v2-default-300"
        :style="{
          top: `${cashbackTooltipPosition.top - 4}px`,
          left: `${cashbackTooltipPosition.arrowLeft}px`,
        }"
      />
      <div
        ref="cashbackTooltipRef"
        class="border-solid pointer-events-none fixed z-[100] w-[240px] max-w-[80vw] -translate-x-1/2 rounded-[12px] border border-white/10 bg-v2-default-300 p-[8px] text-[10px] leading-[14px] text-white shadow-[0_14px_28px_rgba(0,0,0,0.25)]"
        :style="{
          top: `${cashbackTooltipPosition.top}px`,
          left: `${cashbackTooltipPosition.left}px`,
        }"
      >
        {{ CASHBACK_TOOLTIP_TEXT }}
      </div>
    </template>
  </Teleport>
</template>

<style scoped>
/* Os 3 cards mobile têm a mesma largura; o rótulo mais longo ("Freespins
   recebidos", 19 caracteres) não cabe a 10px em telas estreitas, então a
   fonte escala com a largura do card (cqw = 1% da largura do card, medido no
   filho — no próprio container o cqw cai pra viewport) com teto de 10px.
   100cqw − 16px de inset ÷ ~10.6 (largura média por caractere em bold). */
.stat-card-mobile {
  container-type: inline-size;
}

.stat-card-mobile-label {
  font-size: min(10px, calc((100cqw - 16px) / 10.6));
}
</style>
